import { useStrings } from "@1js/localization-components";
import { Column, Grid, Row } from "@1js/pl-card-components";
import {
  Text,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import { useFlightContext } from "../../hooks/useFlightContext";
import { TipCard } from "./TipCard.web";
import type { TipCardsProps } from "./Tips.web.data";
import { TipsTitle } from "./Tips.web.string";

const useStyles = makeStyles({
  container: {
    minHeight: "350px",
    "@media (max-width: 540px)": {
      marginLeft: "0rem",
      marginRight: "0rem",
      paddingRight: "0rem",
      paddingLeft: "0rem",
    },
  },
  titleRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    marginBottom: "12px",
    wordWrap: "break-word",
    zIndex: 1,
    ...shorthands.padding("20px", "0px", "0px", "0px"),
    "@media (max-width: 540px)": {
      marginBottom: "16px",
      ...shorthands.padding("20px", "0px", "0px", "16px"),
    },
  },

  title: {
    color: tokens.colorNeutralForeground1,
    fontSize: "28px",
    fontStyle: "normal",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "36px",
    ...shorthands.margin("0px"),
    wordWrap: "break-word",

    "@media (max-width: 540px)": {
      fontSize: "24px",
      lineHeight: "32px",
    },
  },

  tipsWrapper: {
    marginLeft: "0.75rem",
    marginRight: "0.75rem",
  },

  tips: {
    zIndex: 1,
    alignSelf: "stretch",
    paddingBottom: "1.25rem",
    backgroundImage:
      "radial-gradient(41.23% 46.04% at 28.44% 57.13%, #CAE6FF 0%, rgba(235, 239, 255, 0.00) 100%), radial-gradient(40.02% 40.04% at 73.02% 36.32%, #CAE6FF 0%, rgba(235, 239, 255, 0.00) 100%), radial-gradient(36.05% 36.05% at 50% 58.68%, #EBEFFF 52.73%, rgba(235, 239, 255, 0.00) 100%)",
    "@media (max-width: 1664px)": {
      rowGap: "1rem",
    },
    "@media (max-width: 540px)": {
      ...shorthands.padding("0px", "0px", "0px", "0px"),
    },
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "stretch",
  },

  tip: {
    ...shorthands.padding("0px", "24px", "0px", "0px"),
    "@media (max-width: 1424px)": {
      ...shorthands.flex(0, 0, "100%"),
      ...shorthands.padding("0px", "0px", "0px", "0px"),
      maxWidth: "100%",
    },
  },

  lastTip: {
    ...shorthands.padding("0px", "0px", "0px", "0px"),
    "@media (max-width: 1424px)": {
      ...shorthands.flex(0, 0, "100%"),
      maxWidth: "100%",
    },
  },
});

export const Tips: React.FC<TipCardsProps> = ({ tips }) => {
  const styles = useStyles();
  const localizations = useStrings({
    TipsTitle,
  });
  const { isFlightActive } = useFlightContext();
  return (
    <Grid
      className={styles.container}
      testid="tips"
      goBold={isFlightActive("isGoBoldPromptCardEnabled")}
      isResponsive={isFlightActive("isResponsiveDimensionsEnabled")}
    >
      <Row className={styles.titleRow}>
        <Column size={12}>
          <Text as="h2" align="start" className={styles.title}>
            {localizations.TipsTitle()}
          </Text>
        </Column>
      </Row>
      <div className={styles.tipsWrapper}>
        <Row className={styles.tips}>
          {tips.map((tip, index) => {
            const isLastTip = index === tips.length - 1;
            const tipCardClass = isLastTip ? styles.lastTip : styles.tip;
            return (
              <Column key={tip.id} size={3} className={tipCardClass}>
                <TipCard {...tip} />
              </Column>
            );
          })}
        </Row>
      </div>
    </Grid>
  );
};
